import React, { Suspense, useEffect, useRef } from "react";
import { Switch, Route, useHistory, Redirect } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import { LightenDarkenColor } from "lighten-darken-color";
import { LoaderComponent } from "@iiht/components";
import ProtectedRoute from "./config/ProtectedRoute";
import { getLoginDetailsRequest } from "./store/actions";
import { applicationURL, translator } from "./constants/actionTypes";
import { ROUTES } from "./constants/routesConstants";
import login from "./components/login";
import NotFoundPage from "./components/common/NotFound";
import LayoutView from "./components/common/basicLayout/layoutView";
import { setCustomTenantTheme } from "./utils/genericUtils";
import { localStorageHelper } from "./utils";
import { directLoginStorage } from "./utils/storageHelper";
import { roleName } from "./constants/componentConstants";
import "./App.scss";

const TenantManagementContainer = React.lazy(() =>
  import("./components/Tenant-Management")
);
const UserManagement = React.lazy(() =>
  import("./components/User-Management/User-List")
);
const CreateEditTenant = React.lazy(() =>
  import("./components/Tenant-Management/components/CreateEditTenant")
);
const CreateUser = React.lazy(() =>
  import("./components/User-Management/Create-User")
);
const ViewUserDetails = React.lazy(() =>
  import("./components/User-Management/View-User")
);
const ViewSummaryDetails = React.lazy(() =>
  import("./components/Tenant-Management/components/ViewSummary")
);
const PricingProfileSummary = React.lazy(() =>
  import("./components/Tenant-Management/components/PricingProfileSummary")
);
const rbacManagement = React.lazy(() => import("./components/RbacManagement"));
const RolePermissions = React.lazy(() =>
  import("./components/RbacManagement/components/RolePermissions")
);
const ResourceProvider = React.lazy(() =>
  import("./components/ResourceManager/ResourceProvider")
);
const ManageInstanceSize = React.lazy(() =>
  import("./components/ResourceManager/ManageInstanceSize")
);
const TemplateCreation = React.lazy(() =>
  import("./components/ResourceManager/TemplateCreation")
);
const AddEditResourceProvider = React.lazy(() =>
  import(
    "./components/ResourceManager/ResourceProvider/components/AddEditResourceProvider"
  )
);
const AccountDetailsProvider = React.lazy(() =>
  import(
    "../../webapp/src/components/ResourceManager/ResourceProvider/components/AccountDetailsResource.js"
  )
);
const CreateEditTemplate = React.lazy(() =>
  import(
    "./components/ResourceManager/TemplateCreation/components/CreateEditTemplate"
  )
);
const CreateEditAzureVmForm = React.lazy(() =>
  import(
    "./components/PublicResourceManager/CreateEditAzureVmForm.js"
  )
);
const CreateCatalogue = React.lazy(() =>
  import("./components/MachineCatAdmin/components/CreateCatalogue")
);
const MachineCataloguePage = React.lazy(() =>
  import("./components/MachineCatAdmin")
);
const Btoc = React.lazy(() =>
  import("./components/ResourceManager/ResourceProvider")
);
const MyLabs = React.lazy(() => import("./components/MyLabs"));
const MyLabsVm = React.lazy(() =>
  import("./components/MyLabs/components/myLabsVm")
);
const VmDetails = React.lazy(() =>
  import("./components/MyLabs/components/VmDetails")
);
const UserMachineCatalogue = React.lazy(() =>
  import("./components/MachineCatUser")
);
const LabDetails = React.lazy(() =>
  import("./components/MachineCatUser/components/LabDetails")
);
const NotificationManagement = React.lazy(() =>
  import("./components/NotificationManagement")
);
const NotificationView = React.lazy(() =>
  import("./components/NotificationManagement/components/NotificationView")
);
const RequestManagerContainer = React.lazy(() =>
  import("./components/RequestManager")
);
const ActionLogContainer = React.lazy(() => import("./components/ActionLog"));
const NotificationContainer = React.lazy(() =>
  import("./components/Notifications")
);
const NotificationSettings = React.lazy(() =>
  import("./components/NotificationSettings")
);
const Dashboard = React.lazy(() =>
  import("./components/dashboard/DashboardContainer")
);
const UsageComponent = React.lazy(() => import("./components/Usage"));
const WorkspaceComponent = React.lazy(() =>
  import("./components/WorkspaceManager")
);
const UserWorkspaceComponent = React.lazy(() =>
  import("./components/UserWorkspaceManager")
);
const UserWorkspaceDashboardComponent = React.lazy(() =>
  import("./components/UserWorkspaceDashboardList")
);
const CreateEditWorkspace = React.lazy(() =>
  import("./components/WorkspaceManager/components/CreateEditWorkspace")
);
const ViewWorkspace = React.lazy(() =>
  import("./components/WorkspaceManager/components/ViewWorkspace")
);
const Profile = React.lazy(() =>
  import("./components/profile/components/Profile")
);
const ApiHelp = React.lazy(() =>
  import("./components/Help/components/ApiHelpDocument")
);
const RedocComponent = React.lazy(() =>
  import("./components/Help/components/RedocComponent")
);
const MachineCatHome = React.lazy(() =>
  import("./components/MachineCatUser/MachineCatHome")
);
const CreditBasedLabDetails = React.lazy(() =>
  import("./components/MachineCatUser/components/CreditBasedLabDetails")
);
const PublicLabDetails = React.lazy(() =>
  import("./components/MachineCatAdmin/components/PublicLabDetails")
);
const BatchDetails = React.lazy(() =>
  import("./components/MachineCatAdmin/components/BatchDetails")
);
const WorkspaceBatchDetails = React.lazy(() =>
  import("./components/MachineCatAdmin/components/WorkspaceBatchDetails")
);
const WorkspaceBatchDetailsUsers = React.lazy(() =>
  import("./components/MachineCatAdmin/components/WorkspaceBatchDetailsUsers")
);
const AddBatchAndLab = React.lazy(() =>
  import("./components/MachineCatAdmin/components/AddBatchAndLab")
);
const AddWorkspaceBatchAndLab = React.lazy(() =>
  import("./components/MachineCatAdmin/components/AddWorkspaceBatchAndLab")
);
const IdpRedirect = React.lazy(() =>
  import("./components/common/IdpRedirect/IdpRedirect")
);
const workspaceLogin = React.lazy(() =>
  import("./components/common/WsmLogin/WorkspaceLogin")
);
const LoginRedirect = React.lazy(() =>
  import("./components/common/IdpRedirect/LoginRedirect")
);
const workspaceDashboard = React.lazy(() =>
  import("./components/UserWorkspaceManager/components/WorkspaceManager")
);
const ReportEngine = React.lazy(() =>
  import("./components/ReportEngine/components/ReportEngine")
);
const CustomReport = React.lazy(() =>
  import("./components/ReportEngine/components/CustomReport")
);
const VMConnectWrapper = React.lazy(() =>
  import("./components/VMConnect/VMConnectWrapper")
);
const b2cLabs = React.lazy(() =>
  import("./components/B2C-Labs/components/B2cLabs.js")
);
const b2cLabDetails = React.lazy(() =>
  import("./components/B2C-Labs/components/B2cLabDetails")
);
const Plans = React.lazy(() => import("./components/Plans/components/Plans"));
const TenantCatalogue = React.lazy(() =>
  import("./components/TenantCatalogue/TenantCatalogue")
);
const VmInsight = React.lazy(() => import("./components/VmInsight/VmInsight"));
const Invoices = React.lazy(() => import("./components/Invoices/Invoices"));
const VmInsightDetails = React.lazy(() =>
  import("./components/VmInsight/VmInsightDetails")
);
const B2CPayments = React.lazy(() =>
  import("./components/B2C-payments/components/B2CShoppingCart")
);
const B2CInvoice = React.lazy(() =>
  import("./components/B2C-payments/components/B2CLabInvoice")
);
// For development purpose
const B2CEndUserDashboard = React.lazy(() =>
  import("./components/dashboard/DashboardB2CUser")
);
const ResellerLicense = React.lazy(() =>
  import("./components/ResellerLicense/LicenceList")
);
const CreateResellerLicense = React.lazy(() =>
  import("./components/ResellerLicense/CreateLicense")
);
const ResellrLicenseClientView = React.lazy(() =>
  import("./components/ResellerLicense/ResellerLicenceClientView")
);
const VMDashboardInfo = React.lazy(() =>
  import("./components/dashboard/VMDashboardInfo")
);

const AzDashboardInfo = React.lazy(() =>
  import("./components/dashboard/AzDashboardInfo")
);

const VmUsageAndBilling = React.lazy(() =>
  import("./components/dashboard/components/VmUsageAndBilling")
);

const DashboardResourceGroupVm = React.lazy(() =>
  import("./components/dashboard/components/DashboardResourceGroupVm/DashboardResourceGroupVm")
);

const PublicManageVM = React.lazy(() =>
  import("./components/PublicResourceManager/PublicManageVM")
)

const CloudProviderAzureList = React.lazy(() =>
  import("./components/PublicResourceManager/CloudProviderAzureList")
)

const PublicSkuList = React.lazy(() =>
  import("./components/PublicResourceManager/PublicSkuList")
)

const CloudLabsDashboard = React.lazy(() =>
  import("./components/CloudLabsMonitor/CloudLabsDashboard")
)

const CloudLabsLabDetail = React.lazy(() =>
  import("./components/CloudLabsMonitor/CloudLabsLabDetail")
)

const AppRoute = () => {
  // const dispatch = useDispatch();
  const history = useHistory();

  const token = localStorage.getItem("accessToken");
  // const { loginDetails } = useSelector(({ userAuthentication }) => userAuthentication);
  const tourState = useSelector((state) => state?.portalWalkthrough);
  const { isDarkTheme } = useSelector(({ common }) => common);

  const headerRef = useRef(null);
  // const getTenantUrl = () => {
  //   return window.location.pathname === "/" ? "" : window.location.pathname;
  // };

  useEffect(() => {
    if (!tourState?.run) headerRef?.current?.scrollTo(0, 0);
  }, [tourState?.run]);

  // const dispatchTenantDetails = () => {
  //   let url = getTenantUrl();
  //   localStorageHelper.set("tenantUrl", url);
  //   dispatch(
  //     getLoginDetailsRequest({
  //       tenant_url:
  //         window.location.pathname === "/"
  //           ? applicationURL
  //           : window?.document?.URL.includes("localhost")
  //           ? `${translator.baseURL}${window.location.pathname}`
  //           : window.document.URL,
  //     })
  //   );
  // };
  // useEffect(() => {
  //   if (!localStorage.getItem("accessToken") && !sessionStorage.getItem("tenantDetails")) {
  //     sessionStorage.setItem("tenantDetails", true);
  //     dispatchTenantDetails();
  //   }
  // }, [window?.location?.origin]);

  // useEffect(() => {
  //   if (
  //   //  !localStorage.getItem("accessToken") &&
  //     sessionStorage.getItem("tenantDetails") &&
  //  //   !loginDetails &&
  //    // !window?.document?.URL.includes("standBy")
  //   ) {
  //     dispatchTenantDetails();
  //   }
  // }, [loginDetails]);

  // useEffect(() => {
  //   if (token) {
  //     const root = document.getElementById("root");
  //     const script = document.createElement("script");
  //     script.async = true;
  //     script.innerHTML = `var $zoho = $zoho || {}; $zoho.salesiq = $zoho.salesiq || { widgetcode: "940a2ea7388e8d61889dd6444c289133022d9462f509d1aacb7e03aadeda627cad64e35abf471157b162b56de013b95d", values: {}, 
  //            ready: function(){$zoho.salesiq.floatbutton.position("bottomright") }}; 
  //            var d = document; s = d.createElement("script"); s.type = "text/javascript"; s.id = "zsiqscript"; s.defer = true;
  //            s.src = "https://salesiq.zoho.com/widget"; t = d.getElementsByTagName("script")[0]; t.parentNode.insertBefore(s, t);`;
  //     script.type = "text/javascript";
  //     root.append(script);
  //   }
  // }, [token]);

  useEffect(() => {
    let newScript;
    if (token) {
      const currentPath = history.location.pathname.toLowerCase();
      const isRdpRoute = currentPath.includes("/managevm/mylabs/") ||
        currentPath.includes("/vmdetails/") ||
        currentPath.includes("/mylabs/");

      const root = document.getElementById("root");
      const oldScript = document.getElementById("zsiqscript");
      const isWidgetVisible = !!oldScript;

      if (isRdpRoute) {
        if (oldScript) {
          oldScript.remove();
        }
        try {
          if (window.fcWidget) {
            window.fcWidget.hide();
          }
        } catch (e) { }
      } else if (!isWidgetVisible) {
        newScript = document.createElement("script");
        newScript.id = "zsiqscript";
        newScript.src = "//in.fw-cdn.com/32163537/1154523.js";
        newScript.setAttribute("chat", "true");
        newScript.setAttribute("widgetId", "d32a65e2-c27c-42b8-b022-928f28682262");
        newScript.async = true;
        newScript.type = "text/javascript";

        newScript.onload = () => {
          try {
            if (window.fcWidget) {
              window.fcWidget.show();
            }
          } catch (e) { }
        }

        root.appendChild(newScript);
      }
    }
  }, [token, history.location.pathname]);

  // useEffect(() => {
  //   if (token) {
  //     const root = document.getElementById("root");
  //     // Remove all existing chatbot scripts
  //     const existingScripts = document.querySelectorAll('script[src*="salesiq"]');
  //     existingScripts.forEach(script => {
  //       script.remove();
  //     });
  //     // Create and inject the new Zoho script
  //     const newScript = document.createElement("script");
  //     newScript.src = "//in.fw-cdn.com/32163537/1154523.js";
  //     newScript.setAttribute("chat", "true");
  //     newScript.setAttribute("widgetId", "d32a65e2-c27c-42b8-b022-928f28682262");
  //     newScript.async = true;
  //     newScript.type = "text/javascript";
  //     root.appendChild(newScript);
  //   }
  // }, [token]);

  const setCustomTheme = (theme) => {
    var root = document.querySelector(":root");
    const customTheme = theme ? theme : {};
    setCustomTenantTheme(customTheme, root);
  };

  useEffect(() => {
    const role = JSON.parse(localStorage.getItem("role_name"));
    if (
      role?.[0] !== roleName?.superadmin &&
      role?.[0] !== roleName?.csmadmin &&
      role?.[0] !== roleName?.labadmin
    ) {
      const customTheme = JSON.parse(localStorage.getItem("custom_theme"));
      if (customTheme) {
        setCustomTheme(customTheme);
      }
    }
  }, []);

  useEffect(() => {
    // Set sessionStorage before redirect
    if (window.location.pathname.toLowerCase() === '/dashboard-vm') {
      sessionStorage.setItem('isDashboardVM', '/dashboard-vm');
      history.push('/');
    }
    // Check for dashboard-az redirect
    if (window.location.pathname.toLowerCase() === '/dashboard-cloud') {
      sessionStorage.setItem('isDashboardCloud', '/dashboard-cloud');
      history.push('/');
    }
  }, [history]);

  useEffect(() => {
    // Check for redirect after login
    const redirectPathVM = sessionStorage.getItem('isDashboardVM');
    if (redirectPathVM && token) {
      history.push(redirectPathVM);
      sessionStorage.removeItem('isDashboardVM'); // Clean up after redirect
    }

    const redirectPathAz = sessionStorage.getItem('isDashboardCloud');
    if (redirectPathAz && token) {
      history.push(redirectPathAz);
      sessionStorage.removeItem('isDashboardCloud'); // Clean up after redirect
    }
  }, [token, history]);

  return (
    <>
      <div
        id="app-container"
        className={isDarkTheme ? "content theme-dark" : "content theme-default"}
      >
        <div className="App-content" id="app-content-id" ref={headerRef}>
          <ConnectedRouter history={history}>
            <Suspense fallback={<LoaderComponent />}>
              <LayoutView>
                <Switch>
                  <Route
                    exact
                    path={ROUTES.TENANT_DETAILS_ERROR}
                    component={NotFoundPage}
                  />
                  <Route
                    exact
                    path={["/dashboard-vm", "/Dashboard-vm"]}
                    render={() => {
                      // Only set sessionStorage and redirect if not already authenticated
                      if (!token) {
                        sessionStorage.setItem('isDashboardVM', '/dashboard-vm');
                        return <Redirect to="/" />;
                      }
                      // If already authenticated, render the dashboard-vm component
                      return <VMDashboardInfo />;
                    }}
                  />
                  <Route
                    exact
                    path={["/dashboard-cloud", "/Dashboard-cloud"]}
                    render={() => {
                      // Only set sessionStorage and redirect if not already authenticated
                      if (!token) {
                        sessionStorage.setItem('isDashboardCloud', '/dashboard-cloud');
                        return <Redirect to="/" />;
                      }
                      // If already authenticated, render the dashboard-az component
                      return <AzDashboardInfo />;
                    }}
                  />
                  <Route
                    exact
                    path={["/dashboard-cloud-vm", "/Dashboard-cloud-vm"]}
                    render={({ location }) => {
                      // Only set sessionStorage and redirect if not already authenticated
                      if (!token) {
                        sessionStorage.setItem('isDashboardCloudVM', '/dashboard-cloud-vm');
                        return <Redirect to="/" />;
                      }

                      const resourceGroup = location.state?.resourceGroup;
                      if (!resourceGroup) {
                        return <Redirect to="/dashboard-cloud" />;
                      }

                      // If already authenticated, render the component
                      return <DashboardResourceGroupVm />;
                    }}
                  />
                  <Route
                    exact
                    path={ROUTES.VM_DASHBOARD_INFO}
                    component={VMDashboardInfo}
                  />
                  <Route
                    exact
                    path={ROUTES.AZURE_DASHBOARD_INFO}
                    component={AzDashboardInfo}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.CLOUD_LABS_DASHBOARD}
                    component={CloudLabsDashboard}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.CLOUD_LABS_LAB_DETAIL}/:id`}
                    component={CloudLabsLabDetail}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.PAGE_NOT_FOUND}
                    component={NotFoundPage}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.DASHBOARD}
                    component={Dashboard}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.TENANT_MANAGEMENT}`}
                    component={TenantManagementContainer}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.ADD_TENANT}`}
                    component={CreateEditTenant}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.EDIT_TENANT}/:id`}
                    component={CreateEditTenant}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.EDIT_TENANT}/:tenantId/:profileId`}
                    component={CreateEditTenant}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.USER_MANAGEMENT}`}
                    component={UserManagement}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.ADD_USER}`}
                    component={CreateUser}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.EDIT_USER}:id`}
                    component={CreateUser}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.VIEW_USER_DETAILS}:userId`}
                    component={ViewUserDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.TENANT_VIEW_DETAILS}:id`}
                    component={ViewSummaryDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PRICE}:id`}
                    component={PricingProfileSummary}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.RBAC_MANAGE_HOME}
                    component={rbacManagement}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.RBAC_FEATURE_POLICY}:id`}
                    component={RolePermissions}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.RESOURCE_PROVIDER_HOME}
                    component={ResourceProvider}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.TEMPLATE_CREATION_HOME}:id`}
                    component={TemplateCreation}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PUBLIC_TEMPLATE_CREATION_HOME}:id`}
                    component={TemplateCreation}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.ADD_RESOURCE}
                    component={AddEditResourceProvider}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PUBLIC_ADD_RESOURCE}/:pid`}
                    component={AddEditResourceProvider}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.EDIT_RESOURCE}/:id`}
                    component={AddEditResourceProvider}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PUBLIC_EDIT_RESOURCE}/:id`}
                    component={AddEditResourceProvider}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.ACCOUNT_DETAILS_RESOURCE}/:id`}
                    component={AccountDetailsProvider}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.CREATE_TEMPLATE}/:rid`}
                    component={CreateEditTemplate}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.EDIT_TEMPLATE}/:rid/:tid`}
                    component={CreateEditTemplate}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PUBLIC_CREATE_TEMPLATE}/:rid`}
                    component={CreateEditTemplate}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PUBLIC_EDIT_TEMPLATE}/:rid/:tid`}
                    component={CreateEditTemplate}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PUBLIC_CREATE_AZURE_VM_TEMPLATE}/:rid`}
                    component={CreateEditAzureVmForm}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PUBLIC_CREATE_AZURE_VM_TEMPLATE}/:rid/:tid`}
                    component={CreateEditAzureVmForm}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.MACHINE_CATALOGUE}
                    component={MachineCataloguePage}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.B2C_LABS}
                    component={b2cLabs}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.RESELLERLICENSE}
                    component={ResellerLicense}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.CREATRESELLERLICENSE}
                    component={CreateResellerLicense}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.RESELLERLICENSE_CLIENT_VIEW}
                    component={ResellrLicenseClientView}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.EDIT_RESELLERLICENSE}/:id`}
                    component={CreateResellerLicense}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.B2C_LABS}/:id`}
                    component={b2cLabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.B2C_PLANS}
                    component={Plans}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.TENANT_CATALOGUE}
                    component={TenantCatalogue}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.TENANT_CATALOGUE}/:id`}
                    component={LabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.CREATE_CATALOGUE}`}
                    component={CreateCatalogue}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.MY_LABS}
                    component={MyLabs}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.MANAGE_VM}
                    component={MyLabs}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.MANAGE_INSTANCE_SIZE}
                    component={ManageInstanceSize}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.MANAGE_VM}/:id`}
                    component={LabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.MY_LABS}/vmDetails/:id`}
                    component={VmDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.MY_LABS}/:id`}
                    component={MyLabsVm}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.PUBLIC_RESOURCE_MANAGE_VM}
                    component={PublicManageVM}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.CLOUD_PROVIDER_AZURE_LIST}
                    component={CloudProviderAzureList}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PUBLIC_AZURE_SKU_LIST}/:rid/:tid`}
                    component={PublicSkuList}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.USER_MACHINE_CATALOG}
                    component={MachineCatHome}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.USER_MACHINE_CATALOG}/:id`}
                    component={LabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.NOTIFICATION_MANAGEMENT}
                    component={NotificationManagement}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.NOTIFICATION_MANAGEMENT}/:id`}
                    component={NotificationView}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.REQUEST_MANAGER}
                    component={RequestManagerContainer}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.ACTION_LOG}
                    component={ActionLogContainer}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.NOTIFICATIONS}
                    component={NotificationContainer}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.NOTIFICATION_SETTINGS}
                    component={NotificationSettings}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.USAGE}
                    component={UsageComponent}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.WORKSPACE_MANAGER}
                    component={WorkspaceComponent}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.CREATE_WORKSPACE}`}
                    component={CreateEditWorkspace}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.EDIT_WORKSPACE}/:id`}
                    component={CreateEditWorkspace}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.WORKSPACE_MANAGER}/:id`}
                    component={ViewWorkspace}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES.PROFILE}/:id`}
                    component={Profile}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.USER_WORKSPACE}
                    component={UserWorkspaceComponent}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.VM_USAGE_AND_BILLING}
                    component={VmUsageAndBilling}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES.USER_WORKSPACE_DASHBOARD}
                    component={UserWorkspaceDashboardComponent}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES?.API_HELP}
                    component={ApiHelp}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES?.API_SERVICES_DOCS}
                    component={RedocComponent}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.USER_MACHINE_CATALOG}${ROUTES?.CREDIT_BASED_LAB_DETAILS}/:id`}
                    component={CreditBasedLabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.MACHINE_CATALOGUE}${ROUTES?.CREDIT_BASED_LAB_DETAILS}/:id`}
                    component={CreditBasedLabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.PUBLIC_LAB_DETAILS}/:id`}
                    component={PublicLabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.TENANT_ADMIN_PUBLIC_LAB_DETAILS}/:id`}
                    component={PublicLabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.BATCH_DETAILS}/:id`}
                    component={BatchDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.WORKSPACE_BATCH_DETAILS}/:id`}
                    component={WorkspaceBatchDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.WORKSPACE_BATCH_DETAILS_USERS}/:id`}
                    component={WorkspaceBatchDetailsUsers}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.ADD_BATCH}/:id`}
                    component={AddBatchAndLab}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.EDIT_BATCH}/:id/:bid`}
                    component={AddBatchAndLab}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.ADD_LAB}/:id/:bid`}
                    component={AddBatchAndLab}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.ADD_WORKSPACE_BATCH}/:id`}
                    component={AddWorkspaceBatchAndLab}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.ADD_WORKSPACE_BATCH}/:id/:bid`}
                    component={AddWorkspaceBatchAndLab}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.REPORT_ENGINE}`}
                    component={ReportEngine}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.CUSTOM_REPORT}`}
                    component={CustomReport}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.VM_INSIGHT}`}
                    component={VmInsight}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.INVOICES}`}
                    component={Invoices}
                  />
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.B2C_DASHBOARD}`}
                    component={B2CEndUserDashboard}
                  />{" "}
                  {/* For b2c end user development purpose */}
                  {/* <ProtectedRoute exact path={`${ROUTES?.MY_LABS_B2C}`} component={MyLabsB2CUser} /> For b2c end user development purpose */}
                  <ProtectedRoute
                    exact
                    path={`${ROUTES?.VM_INSIGHT_DETAILS}/:id`}
                    component={VmInsightDetails}
                  />
                  {directLoginStorage.get("isDemandLogin") && (
                    <ProtectedRoute
                      exact
                      path={`${ROUTES?.VM_CONNECT}`}
                      component={VMConnectWrapper}
                    />
                  )}
                  <ProtectedRoute
                    exact
                    path={`/*${ROUTES?.USER_MACHINE_CATALOG}/:id`}
                    component={LabDetails}
                  />
                  <ProtectedRoute
                    exact
                    path={`/*${ROUTES?.USER_MACHINE_CATALOG}`}
                    component={MachineCatHome}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES?.B2C_PAYMENT}
                    component={B2CPayments}
                  />
                  <ProtectedRoute
                    exact
                    path={ROUTES?.B2C_INVOICE}
                    component={B2CInvoice}
                  />
                  {token && !window.location.href.includes("odl") && (
                    <>
                      <Route exact path={localStorageHelper.get("tenantUrl")}>
                        {() => {
                          const redirectPathVM = sessionStorage.getItem('isDashboardVM');
                          if (redirectPathVM) {
                            sessionStorage.removeItem('isDashboardVM');
                            return <Redirect to={redirectPathVM} />;
                          }
                          const redirectPathCloud = sessionStorage.getItem('isDashboardCloud');
                          if (redirectPathCloud) {
                            sessionStorage.removeItem('isDashboardCloud');
                            return <Redirect to={redirectPathCloud} />;
                          }
                          const isMentor = JSON.parse(localStorageHelper.get("is_mentor") || "false");
                          return <Redirect to={isMentor ? "/workspace-manager" : "/dashboard"} />;
                        }}
                      </Route>
                      <Route exact path={"/"}>
                        {() => {
                          const redirectPathVM = sessionStorage.getItem('isDashboardVM');
                          if (redirectPathVM) {
                            sessionStorage.removeItem('isDashboardVM');
                            return <Redirect to={redirectPathVM} />;
                          }
                          const redirectPathCloud = sessionStorage.getItem('isDashboardCloud');
                          if (redirectPathCloud) {
                            sessionStorage.removeItem('isDashboardCloud');
                            return <Redirect to={redirectPathCloud} />;
                          }
                          const isMentor = JSON.parse(localStorageHelper.get("is_mentor") || "false");
                          return <Redirect to={isMentor ? "/workspace-manager" : "/dashboard"} />;
                        }}
                      </Route>
                      <Route exact path={"*"}>
                        {() => {
                          const redirectPathVM = sessionStorage.getItem('isDashboardVM');
                          if (redirectPathVM) {
                            sessionStorage.removeItem('isDashboardVM');
                            return <Redirect to={redirectPathVM} />;
                          }
                          const redirectPathCloud = sessionStorage.getItem('isDashboardCloud');
                          if (redirectPathCloud) {
                            sessionStorage.removeItem('isDashboardCloud');
                            return <Redirect to={redirectPathCloud} />;
                          }
                          const isMentor = JSON.parse(localStorageHelper.get("is_mentor") || "false");
                          return <Redirect to={isMentor ? "/workspace-manager" : "/dashboard"} />;
                        }}
                      </Route>
                    </>
                  )}
                  {(window.location.href.includes("odl") && (
                    <Route
                      exact
                      path={`${window?.location?.pathname}`}
                      component={LoginRedirect}
                    />
                  )) ||
                    null}
                  {window.location.href.includes("standBy") && (
                    <Route
                      exact
                      path={`${window?.location?.pathname}`}
                      component={IdpRedirect}
                    />
                  )}
                  {!localStorage.getItem("accessToken") &&
                    !window.location.pathname.includes("wsm") && (
                      <Route
                        exact
                        path={`${window?.location?.pathname}`}
                        component={LoginRedirect}
                      />
                    )}
                  {!localStorage.getItem("wsmAccessToken") &&
                    !sessionStorage.getItem("wsDetails") &&
                    window.location.pathname.includes("wsm") && (
                      <Route
                        exact
                        path={`${window?.location?.pathname}`}
                        component={LoginRedirect}
                      />
                    )}
                  <Route exact path={`${ROUTES.LOGIN}`} component={login} />
                  <Route
                    exact
                    path={`${ROUTES.WSM_LOGIN}`}
                    component={workspaceLogin}
                  />
                  <ProtectedRoute component={NotFoundPage} />
                </Switch>
              </LayoutView>
            </Suspense>
          </ConnectedRouter>
        </div>
      </div>
    </>
  );
};

export default AppRoute;
